import { FC, useState } from 'react';
import styled, { css } from 'styled-components';

import { CloseButton, StyledDialog } from '@/components/AlertDialogs/Dialog';
import Button from '@/components/Button';
import { Modal } from '@/components/Modal';
import Text from '@/components/Text';

const Dialog = styled(StyledDialog)`
  width: 384px;
`;

const Heading = styled(Text)`
  margin: 0 0 24px;
`;

const List = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  gap: 8px;
`;

const Footer = styled.div`
  margin-top: 32px;
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 16px;
`;

const StyledButton = styled(Button)<{ $isActive: boolean }>`
  font-weight: 600;
  font-size: 14px;
  padding: 0;
  ${({ theme, $isActive }) =>
    $isActive &&
    css`
      border-color: ${theme.colors.purple} !important;
    `};
`;

interface Props {
  onClose: () => void;
  onApprove: (
    shouldCreateRelease: boolean,
    shouldMoveToResearchTool: boolean,
  ) => Promise<void>;
}

enum Option {
  MOVE_TO_BB_DISTRIBUTION,
  MOVE_TO_RESEARCH_TOOL,
  NONE,
}

const ApprovementModal: FC<Props> = ({ onClose, onApprove }) => {
  const [activeOption, setActiveOption] = useState(
    Option.MOVE_TO_RESEARCH_TOOL,
  );

  const onConfirm = () => {
    onApprove(
      activeOption === Option.MOVE_TO_BB_DISTRIBUTION,
      activeOption === Option.MOVE_TO_RESEARCH_TOOL,
    );
  };

  return (
    <Modal zIndex={50}>
      <Dialog
        initial={{ scale: 0.3 }}
        animate={{ scale: 1 }}
        exit={{ scale: 0.3 }}
      >
        <CloseButton onClick={onClose} type="button">
          <svg
            width="14"
            height="14"
            viewBox="0 0 14 14"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M13 1L1 13"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
            <path
              d="M13 13L1 1"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </CloseButton>
        <Heading $type="heading2">
          Would you like to proceed with scheduling this video?
        </Heading>
        <List>
          <StyledButton
            type="button"
            color="secondary"
            $isActive={activeOption === Option.MOVE_TO_BB_DISTRIBUTION}
            onClick={() => setActiveOption(Option.MOVE_TO_BB_DISTRIBUTION)}
          >
            Approve and move to Distribution Draft
          </StyledButton>
          <StyledButton
            type="button"
            color="secondary"
            $isActive={activeOption === Option.MOVE_TO_RESEARCH_TOOL}
            onClick={() => setActiveOption(Option.MOVE_TO_RESEARCH_TOOL)}
          >
            Approve and move to Research Tool
          </StyledButton>
          <StyledButton
            type="button"
            color="secondary"
            $isActive={activeOption === Option.NONE}
            onClick={() => setActiveOption(Option.NONE)}
          >
            Approve without moving
          </StyledButton>
        </List>
        <Footer>
          <Button type="button" onClick={onConfirm}>
            Confirm
          </Button>
          <Button onClick={onClose} color="secondary" type="button">
            Cancel
          </Button>
        </Footer>
      </Dialog>
    </Modal>
  );
};

export default ApprovementModal;
